/* eslint-disable */
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const getDateRange = (startDate, endDate) => {
  const dates = [];
  let currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    dates.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return dates;
};

const groupDeliveryOrdersByDate = (orders, startDate, endDate, deliveryStatusMapping) => {
  const allDates = getDateRange(startDate, endDate);
  const dateFormatOptions = {
    day: "2-digit",
    month: "2-digit",
  };

  const groupedOrders = allDates.reduce((acc, date) => {
    const dateStr = date.toLocaleDateString("fr-FR", dateFormatOptions);
    acc[dateStr] = { delivered: 0, returned: 0, total: 0 };
    return acc;
  }, {});

  orders.forEach((order) => {
    if (order.timeStamp?.seconds) {
      const orderDate = new Date(order.timeStamp.seconds * 1000);
      const orderDateStr = orderDate.toLocaleDateString(
        "fr-FR",
        dateFormatOptions
      );

      const companyStatus = order.CompanyDeleveryStatus || order.companyDeleveryStatus || "";
      const companyStatusStr = String(companyStatus).toLowerCase();

      // Vérifier si le statut correspond à delivered
      const isDelivered = deliveryStatusMapping.delivered.some(
        val => String(val).toLowerCase() === companyStatusStr
      );
      // Vérifier si le statut correspond à returned
      const isReturned = deliveryStatusMapping.returned.some(
        val => String(val).toLowerCase() === companyStatusStr
      );
      // Vérifier si le statut correspond à on_shipping
      const isOnShipping = deliveryStatusMapping.on_shipping.some(
        val => String(val).toLowerCase() === companyStatusStr
      );

      // Compter uniquement les commandes ayant un statut de livraison valide
      if (isDelivered || isReturned || isOnShipping) {
        if (orderDate >= startDate && orderDate <= endDate) {
          if (!groupedOrders[orderDateStr]) {
            groupedOrders[orderDateStr] = { delivered: 0, returned: 0, total: 0 };
          }

          groupedOrders[orderDateStr].total += 1;

          if (isDelivered) {
            groupedOrders[orderDateStr].delivered += 1;
          } else if (isReturned) {
            groupedOrders[orderDateStr].returned += 1;
          }
        }
      }
    }
  });

  const sortedEntries = Object.entries(groupedOrders).sort(
    ([dateA], [dateB]) => {
      const [dayA, monthA] = dateA.split("/").map(Number);
      const [dayB, monthB] = dateB.split("/").map(Number);
      return new Date(2024, monthA - 1, dayA) - new Date(2024, monthB - 1, dayB);
    }
  );

  return sortedEntries;
};

const DeliveryStatisticsChart = ({ orders, dateRange, startDate, endDate, deliveryStatusMapping }) => {
  const { t, i18n } = useTranslation();
  const isDark = document.documentElement.classList.contains("dark");

  const now = new Date();
  let start = new Date(startDate || now);
  let end = new Date(endDate || now);

  const groupedData = groupDeliveryOrdersByDate(orders, start, end, deliveryStatusMapping);

  const labels = groupedData.map(([date]) => date);
  const deliveredRates = groupedData.map(([, data]) => {
    return data.total === 0 ? 0 : Math.round((data.delivered / data.total) * 100);
  });
  const returnedRates = groupedData.map(([, data]) => {
    return data.total === 0 ? 0 : Math.round((data.returned / data.total) * 100);
  });

  const chartData = {
    labels,
    datasets: [
      {
        label: t("delivery.delivery_rate") || "Taux de livraison (%)",
        data: deliveredRates,
        borderColor: "#10b981",
        backgroundColor: "rgba(16, 185, 129, 0.1)",
        borderWidth: 3,
        fill: true,
        tension: 0.4,
        pointRadius: 5,
        pointBackgroundColor: "#10b981",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
      },
      {
        label: t("delivery.return_rate") || "Taux de retour (%)",
        data: returnedRates,
        borderColor: "#ef4444",
        backgroundColor: "rgba(239, 68, 68, 0.1)",
        borderWidth: 3,
        fill: true,
        tension: 0.4,
        pointRadius: 5,
        pointBackgroundColor: "#ef4444",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: "top",
        labels: {
          color: isDark ? "#d1d5db" : "#374151",
          font: { size: 13, weight: "500" },
          padding: 15,
          usePointStyle: true,
          pointStyle: "circle",
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        backgroundColor: isDark ? "#1f2937" : "#fff",
        titleColor: isDark ? "#f3f4f6" : "#000",
        bodyColor: isDark ? "#e5e7eb" : "#000",
        borderColor: isDark ? "#4b5563" : "#e5e7eb",
        borderWidth: 1,
        padding: 12,
        displayColors: true,
        callbacks: {
          label: function (context) {
            return context.dataset.label + ": " + context.parsed.y + "%";
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
          color: isDark ? "#374151" : "#e5e7eb",
        },
        ticks: {
          color: isDark ? "#d1d5db" : "#6b7280",
          font: { size: 12 },
        },
      },
      y: {
        beginAtZero: true,
        max: 100,
        grid: {
          color: isDark ? "#374151" : "#e5e7eb",
          drawBorder: false,
        },
        ticks: {
          color: isDark ? "#d1d5db" : "#6b7280",
          font: { size: 12 },
          callback: function (value) {
            return value + "%";
          },
        },
      },
    },
  };

  return (
    <div className="bg-white dark:!bg-[#181919] rounded-xl shadow-sm p-6 border border-purple-100 dark:!border-[#3c3a3d]">
      <h3 className="text-lg font-semibold text-gray-900 dark:!text-gray-100 mb-6">
        {t("delivery.statistics_chart") || t("delivery.statistics_chart")}
      </h3>
      <Line data={chartData} options={options} />
    </div>
  );
};

export default DeliveryStatisticsChart;
